import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { APP_PATHS } from '@/app/routes/paths';
import {
  PAYMENTS_SIDEBAR_ITEM,
  PRIMARY_NAV_ITEMS,
  REPORT_CARDS_SIDEBAR_ITEM,
  SIDEBAR_NAV_ITEMS,
  type PrimaryNavItem,
  type SidebarNavItem,
} from '@/app/navigation/primaryNavItems';

const SIDEBAR_LABEL_KEYS: Record<string, string> = {
  Home: 'home',
  Explore: 'explore',
  Messages: 'messages',
  Calendar: 'calendar',
  Payments: 'payments',
  'Report Cards': 'reportCards',
};

const PRIMARY_LABEL_KEYS: Record<string, string> = {
  Home: 'home',
  Messages: 'messages',
  Calendar: 'calendar',
};

function translateNavItem<T extends { label: string }>(
  item: T,
  labelKeys: Record<string, string>,
  t: (key: string) => string,
): T {
  const key = labelKeys[item.label];
  return key ? { ...item, label: t(key) } : item;
}

export function usePrimaryNavItems(): readonly PrimaryNavItem[] {
  const { t } = useTranslation('nav');
  return useMemo(
    () => PRIMARY_NAV_ITEMS.map((item) => translateNavItem(item, PRIMARY_LABEL_KEYS, t)),
    [t],
  );
}

export function useSidebarNavItems(
  showPaymentsMenu: boolean,
  showReportCardsMenu: boolean,
): readonly SidebarNavItem[] {
  const { t } = useTranslation('nav');
  return useMemo(() => {
    const items: SidebarNavItem[] = [];

    for (const item of SIDEBAR_NAV_ITEMS) {
      items.push(translateNavItem(item, SIDEBAR_LABEL_KEYS, t));
      if (item.path === APP_PATHS.calendar) {
        if (showPaymentsMenu) {
          items.push(translateNavItem(PAYMENTS_SIDEBAR_ITEM, SIDEBAR_LABEL_KEYS, t));
        }
        if (showReportCardsMenu) {
          items.push(translateNavItem(REPORT_CARDS_SIDEBAR_ITEM, SIDEBAR_LABEL_KEYS, t));
        }
      }
    }

    return items;
  }, [showPaymentsMenu, showReportCardsMenu, t]);
}
